/**
 * Popular Products Add to Bag - Mobile Only
 * Handles variant selection via color swatches for the "Add to Bag" button
 * in the popular products section on mobile devices
 */

;(function () {
  'use strict'

  // Only initialize on mobile screens
  function isMobile() {
    return window.innerWidth <= 575
  }

  /**
   * Get product data from the product card
   */
  function getProductData(cardWrapper) {
    const productHandle = cardWrapper.dataset.product
    if (!productHandle) return null

    // Fetch product JSON from Shopify
    return fetch(`/products/${productHandle}.js`)
      .then((response) => response.json())
      .catch((error) => {
        console.error('Error fetching product data:', error)
        return null
      })
  }

  /**
   * Find variant by selected color option
   */
  function findVariantByColor(product, colorValue) {
    if (!product || !product.variants) return null

    // Try to find exact match first
    const exactMatch = product.variants.find((variant) => {
      return variant.option1 === colorValue || variant.option2 === colorValue || variant.option3 === colorValue
    })

    if (exactMatch) return exactMatch

    // Try case-insensitive match
    const colorLower = colorValue.toLowerCase()
    return product.variants.find((variant) => {
      const opt1 = variant.option1?.toLowerCase()
      const opt2 = variant.option2?.toLowerCase()
      const opt3 = variant.option3?.toLowerCase()
      return opt1 === colorLower || opt2 === colorLower || opt3 === colorLower
    })
  }

  /**
   * Update the Add to Bag button with new variant info
   */
  function updateAddToBagButton(cardWrapper, variant) {
    const variantInput = cardWrapper.querySelector('.popular-products-variant-id')
    const priceElement = cardWrapper.querySelector('.popular-products-price')
    const button = cardWrapper.querySelector('.popular-products-add-to-bag__button')

    if (!variantInput || !priceElement || !button) return

    // Update variant ID
    variantInput.value = variant.id

    // Update price
    const price = Shopify.formatMoney(variant.price, window.theme?.moneyFormat || '${{amount}}')
    priceElement.textContent = price

    // Update button availability
    if (variant.available) {
      button.disabled = false
      button.removeAttribute('aria-disabled')
    } else {
      button.disabled = true
      button.setAttribute('aria-disabled', 'true')
    }
  }

  /**
   * Handle regular color swatch changes
   */
  function handleRegularSwatchChange(input, cardWrapper, productData) {
    const selectedColor = input.value
    const variant = findVariantByColor(productData, selectedColor)

    if (variant) {
      updateAddToBagButton(cardWrapper, variant)
    }
  }

  /**
   * Handle bundle swatch changes (both set and option bundles)
   */
  function handleBundleSwatchChange(input, cardWrapper, productData) {
    // For option bundles, check if there's a variant ID in the data attribute
    const variantId = input.dataset.variantId

    if (variantId) {
      const variant = productData.variants.find((v) => v.id === parseInt(variantId))
      if (variant) {
        updateAddToBagButton(cardWrapper, variant)
        return
      }
    }

    // For set bundles or if no variant ID, try to match by name
    const bundleValue = input.value

    // Try to find variant by searching in title
    const variant = productData.variants.find((v) => {
      return v.title.toLowerCase().includes(bundleValue.toLowerCase())
    })

    if (variant) {
      updateAddToBagButton(cardWrapper, variant)
    } else {
      // Fallback to first available variant
      const firstAvailable = productData.variants.find((v) => v.available)
      if (firstAvailable) {
        updateAddToBagButton(cardWrapper, firstAvailable)
      }
    }
  }

  /**
   * Initialize swatch listeners for a product card
   */
  async function initCardSwatchListeners(cardWrapper) {
    const productData = await getProductData(cardWrapper)
    if (!productData) return

    // Listen for regular color swatch changes
    const regularSwatches = cardWrapper.querySelectorAll(
      '.product-form__controls:not(.js-bundle-swatches) input[type="radio"]'
    )
    regularSwatches.forEach((input) => {
      input.addEventListener('change', () => {
        handleRegularSwatchChange(input, cardWrapper, productData)
      })
    })

    // Listen for set bundle swatch changes
    const setBundleSwatches = cardWrapper.querySelectorAll('.set-bundle-color-input')
    setBundleSwatches.forEach((input) => {
      input.addEventListener('change', () => {
        handleBundleSwatchChange(input, cardWrapper, productData)
      })
    })

    // Listen for option bundle swatch changes
    const optionBundleSwatches = cardWrapper.querySelectorAll('.option-bundle-choice-input')
    optionBundleSwatches.forEach((input) => {
      input.addEventListener('change', () => {
        handleBundleSwatchChange(input, cardWrapper, productData)
      })
    })
  }

  /**
   * Initialize all popular products cards
   */
  function initPopularProductsAddToBag() {
    // Only run on mobile
    if (!isMobile()) return

    const popularProductsSection = document.querySelector('.popular-products-section')
    if (!popularProductsSection) return

    // Find all product cards with the add to bag button
    const cardWrappers = popularProductsSection.querySelectorAll('.card-wrapper[data-product]')

    cardWrappers.forEach((cardWrapper) => {
      // Only initialize if this card has an add to bag button
      const addToBagButton = cardWrapper.querySelector('.popular-products-add-to-bag')
      if (addToBagButton) {
        initCardSwatchListeners(cardWrapper)
      }
    })
  }

  /**
   * Debounce helper
   */
  function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPopularProductsAddToBag)
  } else {
    initPopularProductsAddToBag()
  }

  // Re-initialize on window resize (if switching to/from mobile)
  window.addEventListener('resize', debounce(initPopularProductsAddToBag, 250))

  // Re-initialize when Shopify section is loaded (theme editor)
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.classList.contains('popular-products-section')) {
      initPopularProductsAddToBag()
    }
  })
})()
